import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import API from "@/lib/api";
import { applyTenantTheme } from "@/lib/theme";
import HomePage from "@/pages/HomePage";
import LandingPage from "@/pages/LandingPage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import BrandsPage from "@/pages/BrandsPage";
import BrandDetailPage from "@/pages/BrandDetailPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrdersPage from "@/pages/OrdersPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import OrderShippingPage from "@/pages/OrderShippingPage";
import BillingPage from "@/pages/BillingPage";
import ProfilePage from "@/pages/ProfilePage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminPO from "@/pages/admin/AdminPO";
import AdminPOMonitoring from "@/pages/admin/AdminPOMonitoring";
import AdminBrands from "@/pages/admin/AdminBrands";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCreateOrder from "@/pages/admin/AdminCreateOrder";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminShipping from "@/pages/admin/AdminShipping";
import AdminRekenings from "@/pages/admin/AdminRekenings";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerDetail from "@/pages/admin/AdminCustomerDetail";
import AdminReports from "@/pages/admin/AdminReports";
import AdminMasterVariants from "@/pages/admin/AdminMasterVariants";
import AdminPlaceholder from "@/pages/admin/AdminPlaceholder";
import AdminDiscounts from "@/pages/admin/AdminDiscounts";
import AdminLabels from "@/pages/admin/AdminLabels";
import WishlistPage from "@/pages/WishlistPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-stone-400">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-stone-400">Loading...</div></div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const { t } = useLanguage();
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/shop" element={<HomePage />} />
      <Route path="/products" element={<ProductsPage />} />
      <Route path="/products/:id" element={<ProductDetailPage />} />
      <Route path="/brands" element={<BrandsPage />} />
      <Route path="/brands/:id" element={<BrandDetailPage />} />
      <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
      <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
      <Route path="/orders/:id" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
      <Route path="/orders/:id/shipping" element={<ProtectedRoute><OrderShippingPage /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<LoginPage />} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
      <Route path="/admin/po" element={<AdminRoute><AdminPO /></AdminRoute>} />
      <Route path="/admin/po-monitoring" element={<AdminRoute><AdminPOMonitoring /></AdminRoute>} />
      <Route path="/admin/brands" element={<AdminRoute><AdminBrands /></AdminRoute>} />
      <Route path="/admin/categories" element={<AdminRoute><AdminCategories /></AdminRoute>} />
      <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
      <Route path="/admin/orders/create" element={<AdminRoute><AdminCreateOrder /></AdminRoute>} />
      <Route path="/admin/rekenings" element={<AdminRoute><AdminRekenings /></AdminRoute>} />
      <Route path="/admin/customers" element={<AdminRoute><AdminCustomers /></AdminRoute>} />
      <Route path="/admin/customers/:userId" element={<AdminRoute><AdminCustomerDetail /></AdminRoute>} />
      <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
      <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
      <Route path="/admin/master-variants" element={<AdminRoute><AdminMasterVariants /></AdminRoute>} />
      
      {/* Placeholder Admin Routes */}
      <Route path="/admin/shipping" element={<AdminRoute><AdminShipping /></AdminRoute>} />
      <Route path="/admin/discounts" element={<AdminRoute><AdminDiscounts /></AdminRoute>} />
      <Route path="/admin/expense" element={<AdminRoute><AdminPlaceholder title={t("admin_nav.expense")} /></AdminRoute>} />
      <Route path="/admin/analytics" element={<AdminRoute><AdminPlaceholder title={t("admin_nav.analytics")} /></AdminRoute>} />
      <Route path="/admin/addons" element={<AdminRoute><AdminPlaceholder title={t("admin_nav.addons")} /></AdminRoute>} />
      <Route path="/admin/label" element={<AdminRoute><AdminLabels /></AdminRoute>} />
    </Routes>
  );
}

function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);
  return null;
}

function ThemeBootstrap() {
  useEffect(() => {
    let mounted = true;
    API.get("/tenant")
      .then((r) => {
        if (mounted) applyTenantTheme(r.data || {});
      })
      .catch(() => {
        if (mounted) applyTenantTheme();
      });
    return () => {
      mounted = false;
    };
  }, []);

  return null;
}

function StagingRibbon() {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isStagingHost = ["bajucantik.web.id", "localhost", "127.0.0.1"].includes(host);
  const isStagingEnv = process.env.REACT_APP_ENVIRONMENT === "staging";

  if (!isStagingHost && !isStagingEnv) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-amber-950 text-center text-xs sm:text-sm font-bold tracking-wide py-1 shadow-md pointer-events-none">
      STAGING / TEST ENVIRONMENT — bukan production
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <ThemeBootstrap />
      <StagingRibbon />
      <AuthProvider>
        <CartProvider>
          <LanguageProvider>
            <div className="pt-6 sm:pt-7">
              <AppRouter />
            </div>
            <Toaster />
          </LanguageProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
